import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './DigitalService.scss';
import { PAYMENT_LINKS } from '../../Constants/paymentLinks';
import laptopWorkspace from '../../assets/images/laptop workspace africa.jpg';
import PaymentModal from '../../components/PaymentModal';
import SEO from '../../components/SEO';

const N8N_WEBHOOK = 'https://n8n.kanemx.com/webhook/devis-reseaux-sociaux';

const CAD_PRICES = [
    { amount: '175', currency: 'CAD/mois' },
    { amount: '351', currency: 'CAD/mois' },
    { amount: '702', currency: 'CAD/mois' },
];

const ReseauxSociaux = () => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [selectedLinks, setSelectedLinks] = useState(null);
    const [currency, setCurrency] = useState('CAD');
    const [form, setForm] = useState({
        nom: '', email: '', telephone: '',
        plateformes: [], secteurActivite: '', compteExistant: '',
        objectif: '', budget: '', duree: '', message: ''
    });
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => { window.scrollTo(0, 0); }, []);

    const steps = t('services.social.steps', { returnObjects: true });
    const plans = t('services.social.plans', { returnObjects: true });
    const plateformes = t('services.social.plateformes', { returnObjects: true });
    const comptesOptions = t('services.social.comptes_options', { returnObjects: true });
    const objectifOptions = t('services.social.objectif_options', { returnObjects: true });
    const budgetOptions = t('services.social.budget_options', { returnObjects: true });
    const dureeOptions = t('services.social.duree_options', { returnObjects: true });

    const pricingLinks = [PAYMENT_LINKS.social.starter, PAYMENT_LINKS.social.growth, PAYMENT_LINKS.social.premium];

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: '' });
    };

    const handleCheckbox = (value) => {
        const current = form.plateformes;
        setForm({
            ...form,
            plateformes: current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value]
        });
    };

    const validateStep = () => {
        const newErrors = {};
        if (step === 1) {
            if (!form.nom) newErrors.nom = t('form.required');
            if (!form.email) newErrors.email = t('form.required');
        }
        if (step === 2) {
            if (!form.secteurActivite) newErrors.secteurActivite = t('form.required');
            if (!form.objectif) newErrors.objectif = t('form.required');
        }
        if (step === 3) {
            if (!form.budget) newErrors.budget = t('form.required');
            if (!form.duree) newErrors.duree = t('form.required');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = (nextStep) => { if (validateStep()) setStep(nextStep); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep()) return;
        setLoading(true);
        try {
            await fetch(N8N_WEBHOOK, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, service: 'Réseaux sociaux', dateSoumission: new Date().toISOString() })
            });
            setStatus('success');
        } catch (err) { setStatus('error'); }
        setLoading(false);
    };

    return (
        <div className="ds-page ds-theme-purple">
            <SEO title="Gestion des réseaux sociaux" description="Gestion professionnelle de vos réseaux sociaux. Instagram, Facebook, TikTok, LinkedIn." url="/services/reseaux-sociaux" />

            <div className="ds-hero">
                <img src={laptopWorkspace} alt="" className="ds-hero-img" />
                <div className="ds-hero-overlay" />
                <div className="ds-hero-content">
                    <Link to="/digital" className="ds-back">← {t('services.back')}</Link>
                    <p className="ds-eyebrow">{t('services.social.eyebrow')}</p>
                    <h1 className="ds-hero-title">{t('services.social.hero_title')}</h1>
                    <p className="ds-tagline">{t('services.social.hero_tagline')}</p>
                </div>
            </div>

            <div className="ds-body">
                <div className="ds-inner">
                    <div className="ds-details">
                        <div className="ds-what">
                            <p className="ds-label">{t('services.social.what_label')}</p>
                            <h2>{t('services.social.what_title')}</h2>
                            <div className="ds-steps">
                                {Array.isArray(steps) && steps.map(s => (
                                    <div className="ds-step" key={s.num}>
                                        <span className="ds-step-num">{s.num}</span>
                                        <div><h4>{s.title}</h4><p>{s.desc}</p></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ds-pricing">
                            <div className="ds-pricing-header">
                                <p className="ds-label">{t('services.social.pricing_label')}</p>
                                <div className="ds-toggle">
                                    <button className={currency === 'CAD' ? 'active' : ''} onClick={() => setCurrency('CAD')}>CAD</button>
                                    <button className={currency === 'XOF' ? 'active' : ''} onClick={() => setCurrency('XOF')}>XOF</button>
                                </div>
                            </div>

                            <div className="ds-pricing-cards">
                                {Array.isArray(plans) && plans.map((p, i) => {
                                    const cadPrice = CAD_PRICES[i];
                                    const displayPrice = currency === 'CAD' ? cadPrice.amount : p.price;
                                    const displayCurrency = currency === 'CAD' ? cadPrice.currency : p.currency;
                                    return (
                                        <div className={`ds-pricing-card ${i === 1 ? 'featured' : ''}`} key={p.name}>
                                            <h3>{p.name}</h3>
                                            <div className="ds-price">
                                                <span className="ds-amount">{displayPrice}</span>
                                                {displayCurrency && <span className="ds-currency">{displayCurrency}</span>}
                                            </div>
                                            <ul>{p.features.map(f => <li key={f}><span>✓</span>{f}</li>)}</ul>
                                            <button className="ds-pay-btn" onClick={() => setSelectedLinks(pricingLinks[i])}>
                                                {t('services.social.pay_btn')}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="ds-form-section">
                        <div className="ds-form-header">
                            <p className="ds-label">{t('services.social.form_label')}</p>
                            <h2>{t('services.social.form_title')}</h2>
                            <p>{t('services.social.form_desc')}</p>
                        </div>

                        {status === 'success' ? (
                            <div className="ds-success">
                                <span>✓</span>
                                <h3>{t('services.social.success_title')}</h3>
                                <p>{t('services.social.success_desc')}</p>
                                <button onClick={() => { setStatus(null); setStep(1); }} className="ds-cta-btn">{t('services.social.new_request')}</button>
                            </div>
                        ) : (
                            <form className="ds-form" onSubmit={handleSubmit}>
                                <div className="ds-indicator">
                                    {[1, 2, 3].map(s => (
                                        <div key={s} className={`ds-dot ${step >= s ? 'active' : ''}`}>
                                            <span>{s}</span>
                                        </div>
                                    ))}
                                </div>

                                {step === 1 && (
                                    <div className="ds-step-form">
                                        <h3>{t('services.social.step1_title')}</h3>
                                        <div className="ds-row">
                                            <div className="ds-group">
                                                <label>{t('form.name')}</label>
                                                <input type="text" name="nom" value={form.nom} onChange={handleChange} placeholder="Jean Dupont" />
                                                {errors.nom && <span className="ds-field-error">{errors.nom}</span>}
                                            </div>
                                            <div className="ds-group">
                                                <label>{t('form.email')}</label>
                                                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="jean@exemple.com" />
                                                {errors.email && <span className="ds-field-error">{errors.email}</span>}
                                            </div>
                                        </div>
                                        <div className="ds-group">
                                            <label>{t('form.phone')}</label>
                                            <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} placeholder="+1 514 000 0000" />
                                        </div>
                                        <button type="button" className="ds-cta-btn" onClick={() => handleNext(2)}>{t('form.continue')}</button>
                                    </div>
                                )}

                                {step === 2 && (
                                    <div className="ds-step-form">
                                        <h3>{t('services.social.step2_title')}</h3>
                                        <div className="ds-group">
                                            <label>{t('services.social.secteur_label')}</label>
                                            <input type="text" name="secteurActivite" value={form.secteurActivite} onChange={handleChange} placeholder={t('services.social.secteur_placeholder')} />
                                            {errors.secteurActivite && <span className="ds-field-error">{errors.secteurActivite}</span>}
                                        </div>
                                        <div className="ds-group">
                                            <label>{t('services.social.plateformes_label')}</label>
                                            <div className="ds-checkbox-grid">
                                                {Array.isArray(plateformes) && plateformes.map(p => (
                                                    <label key={p} className="ds-checkbox-item">
                                                        <input type="checkbox" checked={form.plateformes.includes(p)} onChange={() => handleCheckbox(p)} />
                                                        {p}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="ds-row">
                                            <div className="ds-group">
                                                <label>{t('services.social.comptes_label')}</label>
                                                <select name="compteExistant" value={form.compteExistant} onChange={handleChange}>
                                                    <option value="">{t('form.select')}</option>
                                                    {Array.isArray(comptesOptions) && comptesOptions.map(o => <option key={o}>{o}</option>)}
                                                </select>
                                            </div>
                                            <div className="ds-group">
                                                <label>{t('services.social.objectif_label')}</label>
                                                <select name="objectif" value={form.objectif} onChange={handleChange}>
                                                    <option value="">{t('form.select')}</option>
                                                    {Array.isArray(objectifOptions) && objectifOptions.map(o => <option key={o}>{o}</option>)}
                                                </select>
                                                {errors.objectif && <span className="ds-field-error">{errors.objectif}</span>}
                                            </div>
                                        </div>
                                        <div className="ds-nav">
                                            <button type="button" className="ds-back-btn" onClick={() => setStep(1)}>← {t('form.back')}</button>
                                            <button type="button" className="ds-cta-btn" onClick={() => handleNext(3)}>{t('form.continue')}</button>
                                        </div>
                                    </div>
                                )}

                                {step === 3 && (
                                    <div className="ds-step-form">
                                        <h3>{t('services.social.step3_title')}</h3>
                                        <div className="ds-row">
                                            <div className="ds-group">
                                                <label>{t('services.social.budget_label')}</label>
                                                <select name="budget" value={form.budget} onChange={handleChange}>
                                                    <option value="">{t('form.select')}</option>
                                                    {Array.isArray(budgetOptions) && budgetOptions.map(o => <option key={o}>{o}</option>)}
                                                </select>
                                                {errors.budget && <span className="ds-field-error">{errors.budget}</span>}
                                            </div>
                                            <div className="ds-group">
                                                <label>{t('services.social.duree_label')}</label>
                                                <select name="duree" value={form.duree} onChange={handleChange}>
                                                    <option value="">{t('form.select')}</option>
                                                    {Array.isArray(dureeOptions) && dureeOptions.map(o => <option key={o}>{o}</option>)}
                                                </select>
                                                {errors.duree && <span className="ds-field-error">{errors.duree}</span>}
                                            </div>
                                        </div>
                                        <div className="ds-group">
                                            <label>{t('services.social.message_label')}</label>
                                            <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder={t('services.social.message_placeholder')} />
                                        </div>
                                        {status === 'error' && <p className="ds-form-error">{t('form.error')}</p>}
                                        <div className="ds-nav">
                                            <button type="button" className="ds-back-btn" onClick={() => setStep(2)}>← {t('form.back')}</button>
                                            <button type="submit" className="ds-cta-btn" disabled={loading}>
                                                {loading ? t('form.sending') : t('form.submit')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>

            {selectedLinks && <PaymentModal links={selectedLinks} onClose={() => setSelectedLinks(null)} />}
        </div>
    );
};

export default ReseauxSociaux;
